import React from "react";
import "./bottom_bar.css";
import InfoIcon from "./InfoIcon.tsx"; // Import the InfoIcon component
import { useLanguage } from './context/LanguageContext.tsx';

interface BottomBarProps {
  averageTemperatures: (number | null)[]; // 365-day array with some null/NaN values
  temperatureDays: number[]; // Array of DOY indices for available temperature data
}

const BottomBar: React.FC<BottomBarProps> = ({ averageTemperatures = [], temperatureDays = [] }) => {
  const { language } = useLanguage();
  const translations = {
    cz: {
      months: ["Leden", "Únor", "Březen", "Duben", "Květen", "Červen", "Červenec", "Srpen", "Září", "Říjen", "Listopad", "Prosinec"],
      lines: [
        {
          label: "Teploty nad 5°C",
          threshold: 5,
          infoText: "Tzv. velké vegetační období (VVO), kdy průměrná denní teplota dosahuje 5 °C a více. Počátek růstu v nižších polohách ČR. Raší rané dřeviny a dřeviny v podrostu."
        },
        {
          label: "Teploty nad 10°C",
          threshold: 10,
          infoText: "Tzv. hlavní vegetační období (HVO), období s průměrnou denní teplotou 10 °C. Hlavní období růstu, raší a vegetuje většina vegetace na celém území ČR."
        },
        {
          label: "Teploty nad 15°C",
          threshold: 15,
          infoText: "Tzv. vegetační léto (VL), s období průměrnou denní teplotou vzduchu 15 °C. V tomto období rostou všechny dřeviny v případě optimálních podmínek nejintenzivněji."
        }
      ],
      noData: "Žádná data",
      day: "Den"
    },
    en: {
      months: ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"],
      lines: [
        {
          label: "Temperatures above 5°C",
          threshold: 5,
          infoText: "So-called early vegetation season (EVS), when the average daily temperature reaches 5 °C or more. Early vegetation starts in lower elevations."
        },
        {
          label: "Temperatures above 10°C",
          threshold: 10,
          infoText: "So-called main vegetation season (MVS), period with an average daily temperature of 10 °C. Main growth period for most vegetation across the Czech Republic."
        },
        {
          label: "Temperatures above 15°C",
          threshold: 15,
          infoText: "So-called vegetation summer (VS), when the average daily air temperature is around 15 °C. Under optimal conditions, all trees grow most intensively during this time."
        }
      ],
      noData: "No data",
      day: "Day"
    }
  }[language];

  const months = translations.months;

  const lines = translations.lines;

  // Create a 365-day array with temperature values
  const temperaturesByDay = Array.from({ length: 365 }, (_, dayIndex) => {
    const doy = dayIndex + 1; // DOY ranges from 1 to 365

    // Use a safer approach to find the temperature
    let temp: number | null = null;

    // Only proceed if we have valid arrays
    if (Array.isArray(temperatureDays) && Array.isArray(averageTemperatures)) {
      // Find the matching day in temperatureDays
      for (let i = 0; i < temperatureDays.length; i++) {
        if (temperatureDays[i] === doy) {
          temp = averageTemperatures[i] ?? null;
          break;
        }
      }
    }

    return temp;
  });

  const getClassName = (temp: number | null, threshold: number) => {
    if (temp === null || isNaN(Number(temp))) return "grey";
    return temp >= threshold ? "green" : "red";
  };

  // Format temperature for display
  const formatTemperature = (temp: number | null): string => {
    if (temp === null || isNaN(Number(temp))) {
      return "Žádná data";
    }
    return `${Number(temp).toFixed(2)}°C`;
  };

  // Calculate days per month for proper alignment
  const daysPerMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const totalDays = daysPerMonth.reduce((sum, days) => sum + days, 0);

  // Calculate cumulative days for month divider positioning
  const cumulativeDays = daysPerMonth.reduce(
    (acc, days, i) => {
      if (i === 0) return [0, days];
      return [...acc, acc[i] + days];
    },
    [0],
  );

  return (
    <div className="bottom-bar">
      {/* Wrapper for Labels and Temperature Lines */}
      <div className="bottom-bar__wrapper">

        <div className="bottom-bar__logos">
          <div className="bottom-bar__logos-left">
            <a href="https://www.czechglobe.cz" target="_blank" rel="noopener noreferrer">
              <img src="/Logo_Czechglobe/logo_Czechglobe_krivky.png" alt="CzechGlobe Logo" className="bottom-bar__logo" />
            </a>

            <a href="https://www.cesnet.cz" target="_blank" rel="noopener noreferrer">
              <img src="/cesnet_logo/raster/cesnet_RGB_1.jpg" alt="CESNET Logo" className="bottom-bar__logo" />
            </a>
          </div>

          <div className="bottom-bar__logos-right">
            <a href="https://dendronet.cz/" target="_blank" rel="noopener noreferrer">
              <img src="/dendronet_logo/Logo_dendronet.svg" alt="DendroNet Logo" className="bottom-bar__logo" />
            </a>
          </div>
        </div>


        <div className="bottom-bar__labels">
          {lines.map((line, index) => (
            <div key={index} className="bottom-bar__label">
              <span className="info-icon-wrapper">
                <InfoIcon popupText={line.infoText} />
              </span>
              {line.label}
            </div>
          ))}
        </div>


        <div className="bottom-bar__lines">
          {lines.map((line, lineIndex) => (
            <div key={lineIndex} className="bottom-bar__line">
              {temperaturesByDay.map((temp, dayIndex) => (
                <div
                  key={dayIndex}
                  className={`bottom-bar__day ${getClassName(temp, line.threshold)}`}
                  title={`${translations.day} ${dayIndex + 1}: ${formatTemperature(temp)}`}
                ></div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Footer containing Months */}
      <div className="bottom-bar__footer">
        <div className="bottom-bar__months">
          {months.map((month, index) => (
            <div key={index} className="bottom-bar__month">
              {month}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BottomBar;
